<template>
  <div class="dashboard-container">
    <!-- Device Status Indicator - Only for device subscription users -->
    <div v-if="subscriptionType === 'device' && deviceChecking" class="device-status-banner">
      <div class="status-content">
        <div class="status-spinner"></div>
        <span>Connecting to device...</span>
      </div>
    </div>
    <div v-else-if="subscriptionType === 'device' && !isDeviceOnline" class="device-status-banner offline">
      <div class="status-content">
        <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="status-icon" />
        <span>Device offline - some features may be limited</span>
      </div>
    </div>

    <!-- Free User Subscription Banner -->
    <div v-if="subscriptionType === 'free'" class="subscription-banner">
      <div class="banner-content">
        <div class="banner-info">
          <font-awesome-icon :icon="['fas', 'rocket']" class="banner-icon" />
          <div class="banner-text">
            <h4>Free Plan</h4>
            <p>Upgrade to unlock all network management features</p>
          </div>
        </div>
        <button class="upgrade-button" @click="goToBilling">
          Upgrade to Device Plan
        </button>
      </div>
    </div>

    <!-- Network Health Card with Gradient -->
    <div class="network-health-card gradient-card" :class="{ 'free-user-section': subscriptionType === 'free' }">
      <div class="card-content">
        <div class="health-section">
          <h3 class="card-title">Network Health</h3>
          <div class="health-display">
            <div v-if="deviceChecking || !isDeviceOnline" class="health-placeholder">
              <div v-if="deviceChecking" class="placeholder-spinner"></div>
              <span v-else>Device offline</span>
            </div>
            <div v-else class="health-circle">
              <CircularProgress
                :value="networkHealth"
                :max="100"
                label="Health"
                :size="100"
                :strokeWidth="8"
              />
            </div>
          </div>
        </div>
        <div class="speed-section">
          <div class="speed-item">
            <div class="speed-label">
              <font-awesome-icon :icon="['fas', 'upload']" class="speed-icon up" />
              <span>Download</span>
            </div>
            <div class="speed-value">
              <span v-if="deviceChecking">--</span>
              <span v-else-if="!isDeviceOnline">Offline</span>
              <span v-else>{{ downloadSpeed }} <span class="speed-unit">Mbps</span></span>
            </div>
          </div>
          <div class="speed-item">
            <div class="speed-label">
              <font-awesome-icon :icon="['fas', 'download']" class="speed-icon down" />
              <span>Upload</span>
            </div>
            <div class="speed-value">
              <span v-if="deviceChecking">--</span>
              <span v-else-if="!isDeviceOnline">Offline</span>
              <span v-else>{{ uploadSpeed }} <span class="speed-unit">Mbps</span></span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Premium Feature Overlay for Free Users -->
      <div v-if="subscriptionType === 'free'" class="feature-overlay" @click="showUpgradePrompt('NETWORK')">
        <div class="overlay-content">
          <font-awesome-icon :icon="['fas', 'lock']" class="overlay-icon" />
          <span class="overlay-text">Upgrade Required</span>
        </div>
      </div>
    </div>

    <!-- Two Column Stats -->
    <div class="stats-row">
      <div class="stat-card-compact" :class="{ 'free-user-section': subscriptionType === 'free' }">
        <div class="stat-header-compact">
          <span class="stat-title">Devices</span>
          <font-awesome-icon :icon="['fas', 'mobile-alt']" class="stat-icon-compact devices" />
        </div>
        <div class="stat-main-value">
          <span v-if="deviceChecking">--</span>
          <span v-else-if="!isDeviceOnline">--</span>
          <span v-else>{{ activeDevices }}</span>
        </div>
        <div class="stat-subtitle">Connected</div>
        
        <!-- Premium Feature Overlay for Free Users -->
        <div v-if="subscriptionType === 'free'" class="feature-overlay" @click="showUpgradePrompt('DEVICES_MANAGER')">
          <div class="overlay-content">
            <font-awesome-icon :icon="['fas', 'lock']" class="overlay-icon" />
            <span class="overlay-text">Upgrade Required</span>
          </div>
        </div>
      </div>

      <div class="stat-card-compact" :class="{ 'free-user-section': subscriptionType === 'free' }">
        <div class="stat-header-compact">
          <span class="stat-title">Threats</span>
          <font-awesome-icon :icon="['fas', 'ban']" class="stat-icon-compact threats" />
        </div>
        <div class="stat-main-value">
          <span v-if="deviceChecking">--</span>
          <span v-else-if="!isDeviceOnline">--</span>
          <span v-else>{{ threatsBlocked }}</span>
        </div>
        <div class="stat-subtitle">Blocked today</div>
        
        <!-- Premium Feature Overlay for Free Users -->
        <div v-if="subscriptionType === 'free'" class="feature-overlay" @click="showUpgradePrompt('FILTER_BLOCKER')">
          <div class="overlay-content">
            <font-awesome-icon :icon="['fas', 'lock']" class="overlay-icon" />
            <span class="overlay-text">Upgrade Required</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Actions -->
    <h3 class="section-title">Quick Actions</h3>
    <div class="quick-actions-row">
      <div class="quick-action-card" @click="runSpeedTest">
        <span class="action-text">Run Speed Test</span>
        <font-awesome-icon :icon="['fas', 'play']" class="action-icon" />
      </div>
      <div 
        class="quick-action-card" 
        :class="{ 'free-user-section': !canAccessStats }"
        @click="handleStatsClick"
      >
        <span class="action-text">Web Activity Report</span>
        <font-awesome-icon :icon="['fas', 'search']" class="action-icon" />
        
        <!-- Premium Feature Overlay for Free Users -->
        <div v-if="!canAccessStats" class="feature-overlay" @click.stop="showUpgradePrompt('SITE_MANAGER')">
          <div class="overlay-content">
            <font-awesome-icon :icon="['fas', 'lock']" class="overlay-icon" />
            <span class="overlay-text">Upgrade Required</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Active Devices List -->
    <div class="devices-section" :class="{ 'free-user-section': !canAccessDevices }">
      <div class="section-header">
        <h3 class="section-title">Active Devices</h3>
        <button 
          class="see-all-link" 
          :class="{ 'disabled': !canAccessDevices }"
          @click="handleDevicesClick"
        >
          <span>See All</span>
          <font-awesome-icon v-if="canAccessDevices" :icon="['fas', 'chevron-right']" />
          <font-awesome-icon v-else :icon="['fas', 'lock']" />
        </button>
      </div>
      <div class="devices-list-compact">
        <DevicesCompact :devices="recentDevices" />
      </div>
      
      <!-- Premium Feature Overlay for Free Users -->
      <div v-if="!canAccessDevices" class="feature-overlay" @click="showUpgradePrompt('DEVICES_MANAGER')">
        <div class="overlay-content">
          <font-awesome-icon :icon="['fas', 'lock']" class="overlay-icon" />
          <span class="overlay-text">Upgrade Required</span>
        </div>
      </div>
    </div>

    <MessageModal :message="appMessage.text" :type="appMessage.type" ref="messageModal" />
    <LoadingPage :is-visible="loading"/>
  </div>
</template>

<script>
import CircularProgress from "@/components/CircularProgress.vue";
import DevicesCompact from "@/components/DevicesCompact.vue";
import MessageModal from "@/components/MessageModal.vue";
import LoadingPage from "@/components/LoadingPage.vue";
import DeviceAPIService from "@/servicehandlers/DeviceAPIService";
import SubscriptionPermissionService from "@/services/SubscriptionPermissionService";
import {useLoginStore} from "@/stores/loginStore";
const deviceAPIService = new DeviceAPIService()

export default {
  name: 'DashboardPage',
  components: {
    LoadingPage,
    MessageModal,
    CircularProgress,
    DevicesCompact,
  },
  data(){
    return {
      loading: false,
      deviceChecking: true,
      appMessage: {
        text: '',
        type: 'error',
      },
      networkHealth: 100,
      activeDevices: 0,
      downloadSpeed: '545',
      uploadSpeed: '123',
      threatsBlocked: 0,
      recentDevices: [],
    }
  },
  async mounted(){
    // For device users, set device checking status
    if (this.subscriptionType === 'device') {
      this.deviceChecking = !useLoginStore().getDeviceOnline
    }
    
    // Only attempt device data loading for users with device subscription
    if (this.subscriptionType === 'device' && useLoginStore().getDeviceOnline) {
      await this.loadDeviceData()
    }
    
    // Only start device watcher for users with device subscription
    if (this.subscriptionType === 'device') {
      this.startDeviceStatusWatcher()
    }
  },
  computed: {
    isDeviceOnline() {
      return useLoginStore().getDeviceOnline
    },
    subscriptionType() {
      return useLoginStore().getSubscriptionType
    },
    canAccessStats() {
      return SubscriptionPermissionService.canAccessFeature(
        this.subscriptionType,
        'SITE_MANAGER',
        this.isDeviceOnline
      )
    },
    canAccessDevices() {
      return SubscriptionPermissionService.canAccessFeature(
        this.subscriptionType,
        'DEVICES_MANAGER',
        this.isDeviceOnline
      )
    },
  },
  watch: {
    isDeviceOnline(newValue) {
      if (newValue && this.deviceChecking) {
        this.deviceChecking = false
        this.loadDeviceData()
      } else if (!newValue) {
        this.deviceChecking = true
      }
    }
  },
  methods: {
    async loadDeviceData() {
      // Only load device data for users with device subscription
      if (this.subscriptionType !== 'device') {
        return
      }
      
      this.loading = true
      try {
        await this.getNetworkHealth()
        await this.getDevices()
        await this.getThreatsData()
      } catch (e) {
        console.error('Error loading device data:', e)
      }
      this.loading = false
    },
    startDeviceStatusWatcher() {
      const checkInterval = setInterval(() => {
        if (this.subscriptionType === 'device' && useLoginStore().getDeviceOnline && this.deviceChecking) {
          this.deviceChecking = false
          this.loadDeviceData()
          clearInterval(checkInterval)
        }
      }, 1000)
      
      setTimeout(() => {
        clearInterval(checkInterval)
        if (this.deviceChecking) {
          this.deviceChecking = false
        }
      }, 30000)
    },
    async getNetworkHealth(){
      // Only fetch network health for users with device subscription
      if (this.subscriptionType !== 'device') {
        return
      }
      
      try {
        let data = await deviceAPIService.networkHealth(this.$router, useLoginStore())
        this.networkHealth = data.health_percentage
      } catch (e) {
        this.appMessage.text = 'Error: ' + e
        this.$refs.messageModal.open()
      }
    },
    async getDevices() {
      // Only fetch devices for users with device subscription
      if (this.subscriptionType !== 'device') {
        return
      }
      
      try {
        let response = await deviceAPIService.getDevices(this.$router, useLoginStore());
        this.recentDevices = response.devices.slice(0, 4);
        this.activeDevices = response.devices.filter(d => d.active).length;
      } catch (e) {
        console.error('Error fetching devices:', e);
      }
    },
    async getThreatsData() {
      // Only fetch threats data for users with device subscription
      if (this.subscriptionType !== 'device') {
        return
      }
      
      try {
        let response = await deviceAPIService.getDeviceTrackingData(this.$router, useLoginStore());
        // Extract threats blocked count from tracking data
        if (response && response.blocked_requests_today) {
          this.threatsBlocked = response.blocked_requests_today;
        }
      } catch (e) {
        console.error('Error fetching threats data:', e);
      }
    },
    runSpeedTest() {
      this.$router.push({ name: 'speedtest' })
    },
    viewStats() {
      this.$router.push({ name: 'siteManager' })
    },
    goToDevices() {
      this.$router.push({ name: 'devicesManager' })
    },
    goToUpgrade() {
      this.$router.push({ name: 'billingSubscription' })
    },
    handleStatsClick() {
      if (this.canAccessStats) {
        this.viewStats()
      } else {
        this.showUpgradePrompt('SITE_MANAGER')
      }
    },
    handleDevicesClick() {
      if (this.canAccessDevices) {
        this.goToDevices()
      } else {
        this.showUpgradePrompt('DEVICES_MANAGER')
      }
    },
    showUpgradePrompt(featureName) {
      // Access the Menus component through the parent chain and call showUpgradeModal
      let parent = this.$parent;
      while (parent && !parent.showUpgradeModal) {
        parent = parent.$parent;
      }
      if (parent && parent.showUpgradeModal) {
        // Create a dummy route name based on the feature
        const routeMap = {
          'SITE_MANAGER': 'siteManager',
          'DEVICES_MANAGER': 'devicesManager',
          'NETWORK': 'network',
          'FILTER_BLOCKER': 'filterBlocker'
        };
        const routeName = routeMap[featureName] || 'dashboard';
        parent.showUpgradeModal(routeName);
      }
    },
    goToBilling() {
      this.$router.push({
        name: 'billingSubscription'
      })
    },
  },
}
</script>

<style scoped>
.dashboard-container {
  padding: 0;
  max-width: 100%;
}

/* Subscription Banner Styles */
.subscription-banner {
  background: var(--network-health-gradient);
  border-radius: 16px;
  margin-bottom: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  position: relative;
}

.subscription-banner::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3), transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1), transparent 50%);
  pointer-events: none;
}

.banner-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  position: relative;
  z-index: 2;
}

.banner-info {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.banner-icon {
  font-size: 2rem;
  color:  var(--text-primary);
  opacity: 0.9;
}

.banner-text h4 {
  color: var(--text-primary);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 4px 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.banner-text p {
  color: var(--text-primary);
  font-size: 0.875rem;
  margin: 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.upgrade-button {
  background: var(--network-health-gradient);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  color: var(--text-primary);
  padding: 12px 20px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  white-space: nowrap;
}

.upgrade-button:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}

/* Mobile responsive */
@media (max-width: 768px) {
  .banner-content {
    flex-direction: column;
    gap: 16px;
    text-align: center;
    padding: 20px;
  }
  
  .banner-info {
    flex-direction: column;
    gap: 12px;
    text-align: center;
  }
  
  .upgrade-button {
    width: 100%;
    max-width: 250px;
  }
}

/* Network Health Card with Gradient */
.network-health-card {
  margin-bottom: 20px;
}

/* Base network health card styles */
.network-health-card.gradient-card {
  border: none !important;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  position: relative;
  background: linear-gradient(174deg, #F9F9F9 0%, #E6E2DF 100%) !important;
}

/* Network Health card base gradient - multiple high specificity selectors */
.dashboard-container .network-health-card.gradient-card,
div.network-health-card.gradient-card,
.network-health-card.gradient-card,
body .network-health-card.gradient-card,
html .network-health-card.gradient-card {
  background: var(--network-health-gradient) !important;
  background-color: #F9F9F9 !important;
}

/* Dark mode text colors for network health card */
[data-theme="dark"] .network-health-card .card-title {
  color: hsl(215, 15%, 75%);
}

[data-theme="dark"] .network-health-card .speed-label {
  color: hsl(215, 15%, 70%);
}

[data-theme="dark"] .network-health-card .speed-value {
  color: hsl(215, 10%, 85%);
}

[data-theme="dark"] .network-health-card .speed-unit {
  color: hsl(215, 15%, 65%);
}

.card-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  align-items: center;
}

.health-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.card-title {
  font-size: 0.875rem;
  font-weight: 500;
  color: hsl(215, 15%, 45%);
  margin: 0 0 16px 0;
  font-family: 'Poppins', 'Inter', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.health-display {
  display: flex;
  justify-content: center;
  align-items: center;
}

.health-circle {
  flex-shrink: 0;
}


.speed-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-end;
}

.speed-item {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.speed-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: hsl(215, 15%, 45%);
  font-weight: 500;
}

.speed-icon {
  font-size: 0.75rem;
}

.speed-icon.up {
  color: hsl(150, 60%, 35%);
}

.speed-icon.down {
  color: hsl(215, 60%, 45%);
}

.speed-value {
  font-size: 1.5rem;
  font-weight: 700;
  font-family: 'Oxanium', 'Inter', sans-serif;
  color: hsl(215, 20%, 20%);
  line-height: 1;
}

.speed-unit {
  font-size: 0.875rem;
  font-weight: 500;
  color: hsl(215, 15%, 45%);
}

/* Two Column Stats */
.stats-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card-compact {
  background: var(--color-card);
  border: 1px solid var(--color-card-border);
  border-radius: 12px;
  padding: 16px;
  box-shadow: var(--shadow-card);
}

.stat-header-compact {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.stat-title {
  font-size: 0.875rem;
  color: var(--text-muted);
  font-weight: 400;
}

.stat-icon-compact {
  font-size: 1.25rem;
  opacity: 0.6;
}

.stat-icon-compact.devices {
  color: #6BAB90;
}

.stat-icon-compact.threats {
  color: #0F66AB;
}

.stat-main-value {
  font-size: 2rem;
  font-weight: 700;
  font-family: 'Oxanium', 'Inter', sans-serif;
  color: var(--text-primary);
  line-height: 1;
  margin: 8px 0 4px 0;
}

.stat-subtitle {
  font-size: 0.75rem;
  color: var(--text-muted);
}

/* Quick Actions */
.section-title {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
  margin: 0 0 12px 0;
  font-family: 'Poppins', 'Inter', sans-serif;
}

.quick-actions-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 24px;
}

.quick-action-card {
  background: #6BAB90;
  border: 1px solid #6BAB90;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: var(--shadow-card);
}

.quick-action-card:hover {
  background: #5A9A7A;
  transform: translateY(-2px);
  box-shadow: var(--shadow-hover);
}

.action-text {
  font-size: 0.875rem;
  color: white;
  font-weight: 500;
}

.action-icon {
  font-size: 1.25rem;
  color: white;
}

.quick-action-card:hover .action-icon {
  color: white;
}

.quick-action-card:hover .action-text {
  color: white;
}

.quick-action-card:active {
  background: #4A8A6A;
  transform: translateY(0);
}

.quick-action-card:active .action-icon,
.quick-action-card:active .action-text {
  color: white;
}

/* Devices Section */
.devices-section {
  background: var(--color-card);
  border: 1px solid var(--color-card-border);
  border-radius: 16px;
  padding: 20px;
  box-shadow: var(--shadow-card);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.see-all-link {
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 0.875rem;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.see-all-link:hover {
  color: var(--text-primary);
  background: var(--color-background);
}

.see-all-link svg {
  font-size: 0.75rem;
}

.devices-list-compact {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Mobile Responsive - Keep two columns */
@media (max-width: 768px) {
  .card-content {
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    align-items: start;
  }

  .health-section {
    align-items: flex-start;
  }

  .health-display {
    align-items: flex-start;
    gap: 8px;
  }

  .health-circle {
    order: 1;
  }

  .health-text {
    order: 2;
  }

  .speed-section {
    align-items: flex-end;
    gap: 12px;
  }

  .speed-item {
    align-items: flex-end;
  }

  .health-value {
    font-size: 1.5rem;
  }

  .speed-value {
    font-size: 1.125rem;
  }
}

@media (max-width: 480px) {
  .dashboard-container {
    padding: 0;
  }

  .gradient-card {
    padding: 18px;
    margin-bottom: 16px;
  }

  .card-content {
    gap: 12px;
  }

  .card-title {
    font-size: 0.8rem;
    margin-bottom: 10px;
  }

  .health-display {
    gap: 12px;
  }

  .health-value {
    font-size: 1.75rem;
  }

  .speed-value {
    font-size: 1rem;
  }

  .speed-section {
    gap: 10px;
  }

  .stats-row {
    gap: 12px;
    margin-bottom: 16px;
  }

  .stat-card-compact {
    padding: 12px;
  }

  .stat-main-value {
    font-size: 1.75rem;
  }

  .quick-actions-row {
    gap: 10px;
    margin-bottom: 16px;
  }

  .quick-action-card {
    padding: 12px;
  }

  .action-text {
    font-size: 0.8rem;
  }

  .action-icon {
    font-size: 1.125rem;
  }

  .devices-section {
    padding: 16px;
  }

  .section-title {
    font-size: 0.8rem;
    margin-bottom: 10px;
  }
}

@media (max-width: 375px) {
  .network-health-card {
    padding: 12px;
  }

  .card-title {
    font-size: 0.85rem;
    margin-bottom: 12px;
  }

  .speed-value {
    font-size: 1rem;
  }

  .speed-label {
    font-size: 0.8rem;
  }

  .stats-row {
    gap: 8px;
  }

  .stat-card-compact {
    padding: 10px;
  }

  .stat-title {
    font-size: 0.8rem;
  }

  .stat-main-value {
    font-size: 1.5rem;
  }

  .stat-subtitle {
    font-size: 0.7rem;
  }

  .stat-icon-compact {
    font-size: 1.125rem;
  }

  .quick-actions-row {
    gap: 8px;
  }

  .quick-action-card {
    padding: 10px;
  }

  .action-text {
    font-size: 0.75rem;
  }

  .action-icon {
    font-size: 1rem;
  }

  .devices-section {
    padding: 12px;
  }
}

/* Device Status Banner */
.device-status-banner {
  background: linear-gradient(135deg, #4CAF50, #45a049);
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
}

.device-status-banner.offline {
  background: linear-gradient(135deg, #FF9800, #F57C00);
  box-shadow: 0 2px 8px rgba(255, 152, 0, 0.3);
}

.status-content {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  font-weight: 500;
}

.status-icon {
  font-size: 1rem;
}

.status-spinner, .placeholder-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.placeholder-spinner {
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top: 2px solid var(--text-muted);
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.health-placeholder {
  width: 100px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  color: var(--text-muted);
  font-size: 0.75rem;
  text-align: center;
}

/* Dark mode adjustments for status banners */
[data-theme="dark"] .device-status-banner {
  background: linear-gradient(135deg, #2E7D32, #1B5E20);
}

[data-theme="dark"] .device-status-banner.offline {
  background: linear-gradient(135deg, #E65100, #BF360C);
}

/* Subscription Status Banners */
.subscription-status-banner {
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.subscription-status-banner.free {
  background: linear-gradient(135deg, #9C27B0, #7B1FA2);
  color: white;
}

.subscription-status-banner.paid {
  background: linear-gradient(135deg, #2196F3, #1976D2);
  color: white;
}

.upgrade-btn-small {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: auto;
}

.upgrade-btn-small:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-1px);
}

/* Disabled states */
.quick-action-card.disabled {
  background: var(--color-card-border) !important;
  opacity: 0.6;
  cursor: not-allowed;
  color: var(--text-primary);
}

.quick-action-card.disabled:hover {
  background: var(--color-card-border) !important;
  transform: none;
}

.quick-action-card.disabled .action-text,
.quick-action-card.disabled .action-icon {
  color: var(--text-secondary) !important;
}

.see-all-link.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

.see-all-link.disabled:hover {
  color: var(--text-muted);
  background: transparent;
}

/* Free User Overlay Styles */
.free-user-section {
  position: relative;
}

.feature-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: inherit;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
}

.feature-overlay:hover {
  background: rgba(0, 0, 0, 0.8);
}

.overlay-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: white;
  text-align: center;
}

.overlay-icon {
  font-size: 2rem;
  opacity: 0.9;
}

.overlay-text {
  font-size: 0.875rem;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

/* Dark mode adjustments for overlays */
[data-theme="dark"] .feature-overlay {
  background: rgba(0, 0, 0, 0.8);
}

[data-theme="dark"] .feature-overlay:hover {
  background: rgba(0, 0, 0, 0.9);
}

/* Mobile responsive adjustments for overlays */
@media (max-width: 768px) {
  .overlay-icon {
    font-size: 1.5rem;
  }
  
  .overlay-text {
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  .overlay-icon {
    font-size: 1.25rem;
  }
  
  .overlay-text {
    font-size: 0.75rem;
  }
}
</style>